import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Alert, Spin } from "antd";
import { ArrowLeftOutlined, MessageOutlined } from "@ant-design/icons";
import ChatWindow from "./ChatWindow/ChatWindow";
import UserList from "./Users/UsersList";
import "./Chat.css";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { SocketContext } from "../../context/SocketContext";
import { isAuthenticated } from "../Auth/auth";
import { useIsCompact } from "../../design-system/useMediaQuery";
import { CommunicationView } from "../Communication/components/CommunicationView.jsx";
import { communicationApi, mapCommunicationError } from "../Communication/api/communicationApi.js";
import { useTranslation } from "react-i18next";

const ChatPage = () => {
  const { t } = useTranslation("dashboard");
  const socket = useContext(SocketContext);
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const queryConversationId = searchParams.get("conversationId");
  const queryReceiver = searchParams.get("receiver");
  const isLegacyDebug = searchParams.get("legacy") === "true";

  const receiverId =
    queryReceiver && queryReceiver !== "undefined" && queryReceiver !== "null"
      ? queryReceiver
      : null;

  const sender = isAuthenticated();
  const isCompact = useIsCompact();

  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [conversationsError, setConversationsError] = useState(null);

  const [activeConversationId, setActiveConversationId] = useState(queryConversationId || null);
  const [selectedUser, setSelectedUser] = useState(receiverId || null);
  const [threadOpen, setThreadOpen] = useState(Boolean(queryConversationId || receiverId));
  const [receiver, setReceiver] = useState(null);
  const [conversationUnavailable, setConversationUnavailable] = useState(false);

  // Legacy state preserved internally for legacy debug mode
  const [legacyUsers, setLegacyUsers] = useState([]);
  const [legacyMessages, setLegacyMessages] = useState([]);

  const fetchConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      setConversationsError(null);
      const res = await communicationApi.listConversations({ limit: 50 });
      const list = res?.conversations || [];
      setConversations(list);
      return list;
    } catch (err) {
      console.warn("Failed to load conversations:", err?.message);
      setConversationsError(mapCommunicationError(err));
      return [];
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  // 1. Initial Load & Refresh Restoration
  useEffect(() => {
    let isCancelled = false;

    fetchConversations().then(loadedList => {
      if (isCancelled) return;

      if (queryConversationId) {
        // Authoritative validation: check if requested conversation exists in accessible list
        const match = loadedList.find(c => String(c._id) === String(queryConversationId));
        if (match) {
          setActiveConversationId(match._id);
          setThreadOpen(true);
          setConversationUnavailable(false);
          if (match.partner) {
            setReceiver(match.partner);
            setSelectedUser(match.partner._id);
          }
        } else {
          // If not in first page, verify via listMessages endpoint
          communicationApi
            .listMessages(queryConversationId, { limit: 1 })
            .then(() => {
              if (isCancelled) return;
              setActiveConversationId(queryConversationId);
              setThreadOpen(true);
              setConversationUnavailable(false);
            })
            .catch(() => {
              if (isCancelled) return;
              setConversationUnavailable(true);
              setActiveConversationId(null);
            });
        }
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [queryConversationId, fetchConversations]);

  const getUserById = async (id, role) => {
    if (!id || id === "undefined" || id === "null") return;
    try {
      const res = await axios.get(
        role === 1
          ? `${process.env.REACT_APP_BACKEND_URL}/employees/employee/${id}`
          : `${process.env.REACT_APP_BACKEND_URL}/users/user/${id}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );
      if (res.status === 200) {
        setReceiver(res.data);
      }
    } catch (err) {
      console.warn("getUserById error:", err?.message);
    }
  };

  // 2. Legacy Socket Event Handlers (Preserved Internally)
  useEffect(() => {
    if (!socket) return undefined;
    socket.emit("users");
    if (receiverId) socket.emit("messages", { receiver: receiverId });

    const onUsers = list => setLegacyUsers(list);
    const onMessages = list => setLegacyMessages(list);

    const onChatUpdated = ({ from } = {}) => {
      socket.emit("users");
      fetchConversations();
      const open = selectedUser?._id ?? selectedUser;
      if (open && String(open) === String(from)) {
        socket.emit("messages", { receiver: from });
      }
    };

    socket.on("users", onUsers);
    socket.on("messages", onMessages);
    socket.on("chat:updated", onChatUpdated);

    return () => {
      socket.off("users", onUsers);
      socket.off("messages", onMessages);
      socket.off("chat:updated", onChatUpdated);
    };
  }, [socket, selectedUser, receiverId, fetchConversations]);

  // 3. User Selection / Opening Conversation
  const handleSelectUser = (user, role, chat) => {
    let partnerId = null;
    let initialUserObj = null;

    if (typeof user === "object" && user !== null) {
      partnerId = user._id || user.id;
      initialUserObj = user;
    } else {
      partnerId = user;
    }

    if (!partnerId || partnerId === "undefined" || partnerId === "null") {
      return;
    }

    setSelectedUser(partnerId);
    setThreadOpen(true);
    setConversationUnavailable(false);

    if (initialUserObj && (initialUserObj.firstName || initialUserObj.name)) {
      setReceiver({
        _id: partnerId,
        firstName: initialUserObj.firstName || initialUserObj.name || "",
        lastName: initialUserObj.lastName || "",
        picture: initialUserObj.picture,
        role: role ?? initialUserObj.role
      });
    }

    getUserById(partnerId, role);

    // If existing modern conversation
    if (chat?._id && chat?.type) {
      setActiveConversationId(chat._id);
      history.replace({ search: `?conversationId=${chat._id}` });
    } else {
      // Create or retrieve direct conversation via authorized endpoint
      communicationApi
        .createDirectConversation({ participantIdentityId: partnerId })
        .then(res => {
          if (res?.conversation?._id) {
            setActiveConversationId(res.conversation._id);
            history.replace({ search: `?conversationId=${res.conversation._id}` });
            fetchConversations();
          }
        })
        .catch(err => {
          console.warn("Failed to create direct conversation:", err?.message);
        });
    }

    if (socket) {
      socket.emit("messages", { receiver: partnerId });
    }
  };

  const sendLegacyMessage = async (message, file) => {
    const senderId = sender?._id;
    socket.emit("sendMessage", {
      receiverId: selectedUser?._id ?? selectedUser,
      message,
      file
    });
    setLegacyMessages(prev => [
      ...prev,
      { sender: senderId, receiver: selectedUser, message, file }
    ]);
  };

  const showList = !isCompact || !threadOpen;
  const showThread = !isCompact || threadOpen;
  const onBack = isCompact ? () => setThreadOpen(false) : null;

  const activeConv = conversations.find(c => String(c._id) === String(activeConversationId));
  const conversationTitle = receiver
    ? `${receiver.firstName || ""} ${receiver.lastName || ""}`.trim()
    : activeConv?.partner
      ? `${activeConv.partner.firstName || ""} ${activeConv.partner.lastName || ""}`.trim()
      : t("Chat");

  // Display modern conversations, or fallback to socket users if no modern conversations exist yet
  const displayedItems = conversations.length > 0 ? conversations : legacyUsers;

  return (
    <div className={`chat-page${isCompact ? " chat-page--compact" : ""}`}>
      {showList && (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, minWidth: 0 }}>
          {conversationsError && (
            <div style={{ padding: "8px 12px" }}>
              <Alert
                type="warning"
                message={t("Unable to load conversations. Please try again.") || conversationsError}
                closable
              />
            </div>
          )}
          <UserList
            users={displayedItems}
            selectedUser={activeConversationId || selectedUser}
            onSelectUser={handleSelectUser}
          />
        </div>
      )}

      {showThread && (
        <div className="chat-page__main">
          {conversationUnavailable ? (
            <div className="chat-page__empty" style={{ textAlign: "center", padding: "40px" }}>
              <Alert
                type="info"
                message={t("Conversation unavailable") || "Diese Konversation ist nicht verfügbar"}
                description={t("Please select another conversation from the list.") || "Bitte wählen Sie eine Konversation aus der Liste."}
                showIcon
              />
            </div>
          ) : isLegacyDebug && receiver ? (
            <ChatWindow
              sender={sender}
              receiver={receiver}
              messages={legacyMessages}
              onSendMessage={sendLegacyMessage}
              onBack={onBack}
            />
          ) : activeConversationId ? (
            <CommunicationView
              conversationId={activeConversationId}
              title={conversationTitle}
              receiver={receiver}
              onBack={onBack}
            />
          ) : (
            <>
              {onBack && (
                <div className="chat-page__bar">
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined className="chat-page__back-icon" />}
                    onClick={onBack}
                    aria-label={t("Back")}
                  />
                </div>
              )}
              <div className="chat-page__empty">
                <div className="chat-page__empty-card">
                  <div className="chat-page__empty-icon">
                    <MessageOutlined />
                  </div>
                  <div className="chat-page__empty-title">
                    {t("Please select a chat") || "Keine Konversation ausgewählt"}
                  </div>
                  <p className="chat-page__empty-subtitle">
                    {t("Choose a conversation from the list to start messaging.") ||
                      "Wählen Sie einen Kontakt oder eine Konversation aus der linken Leiste aus."}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatPage;
